import React, { useState} from 'react';

const intercept = -1.4669;

// Main effects (index = level - 1)
const km    = [0, 0.1263, 0.1730, 0.2717, 0.3337];
const zone  = [0, -0.3176, -0.4426, -0.7156, -0.4371, -0.6421, -0.8834];
const make  = [0, -0.2064, -0.3564, -1.0127, 0.1123, -0.5920, -0.4285, 0.0820, -0.3662];
const bonus = [0, -0.5466, -0.8286, -1.0236, -1.1896, -1.2906, -1.6962];

// Zone x Bonus: zoneBonus[zone - 1][bonus - 1]   (7 x 7)
const zoneBonus = [
  [0, 0,       0,       0,       0,       0,       0],
  [0, 0.0769,  0.0351,  0.0757,  0.1057,  0.0579,  0.1346],
  [0, 0.0138, -0.0241,  0.0687,  0.0678,  0.0492,  0.1137],
  [0, 0.1401,  0.1048,  0.1210,  0.1438,  0.1136,  0.2052],
  [0, 0.0629,  0.0058,  0.0750,  0.1550,  0.0949,  0.1977],
  [0, 0.0734,  0.0413,  0.0797,  0.1106,  0.0328,  0.2163],
  [0, 0.1633,  0.0986, -0.0415,  0.2878,  0.0735,  0.2471],
];

// Make x Bonus: makeBonus[make - 1][bonus - 1]   (9 x 7)
const makeBonus = [
  [0, 0,       0,       0,       0,       0,       0],
  [0, 0.1267,  0.0970,  0.1505,  0.2358,  0.2956,  0.2867],
  [0, 0.0999,  0.1033, -0.1320,  0.0872,  0.3000,  0.1460],
  [0, -0.0474, 0.1139,  0.3038,  0.1381,  0.2789,  0.4379],
  [0, 0.0804,  0.0548, -0.0451,  0.0047,  0.0301,  0.0035],
  [0, 0.0716,  0.1410,  0.1817,  0.1711,  0.3447,  0.3082],
  [0, 0.1299,  0.2937,  0.2596,  0.2867,  0.2788,  0.3480],
  [0, -0.2515, 0.1688, -0.1277, -0.1080,  0.2342,  0.0199],
  [0, -0.0050, 0.1053,  0.1390,  0.1983,  0.2607,  0.2629],
];

// Make x Kilometres: makeKm[make - 1][km - 1]   (9 x 5)
const makeKm = [
  [0, 0,       0,       0,       0],
  [0, 0.0404,  0.1022,  0.0658,  0.2467],
  [0, -0.1298, 0.0012,  0.2452,  0.1141],
  [0, 0.2190,  0.1817,  0.1583,  0.1451],
  [0, -0.0044, 0.0178,  0.0531,  0.0883],
  [0, -0.0123, 0.0603,  0.0305,  0.1241],
  [0, 0.0618,  0.1758,  0.1768,  0.2715],
  [0, -0.0872, -0.1884, -0.1500, -0.0362],
  [0, 0.0999,  0.1736,  0.1435,  0.2730],
];


// Max level for each input
const MAX = { km: 5, zone: 7, make: 9, bonus: 7 };
 
// Breaks the linear predictor into its individual contributions
function computeBreakdown(k, z, m, b) {
  const main = {
    km: km[k - 1],
    zone: zone[z - 1],
    make: make[m - 1],
    bonus: bonus[b - 1],
  };
  const interactions = {
    zoneBonus: zoneBonus[z - 1][b - 1],
    makeBonus: makeBonus[m - 1][b - 1],
    makeKm: makeKm[m - 1][k - 1],
  };
  const sum =
    intercept +
    Object.values(main).reduce((a, v) => a + v, 0) +
    Object.values(interactions).reduce((a, v) => a + v, 0);
  return { main, interactions, sum, rate: Math.exp(sum) };
}
 
const clamp = (v, max) => Math.min(max, Math.max(1, v));
 
function NumberInput({ label, value, max, onChange }) {
  return (
    <label>
      {label}:&nbsp;
      <input
        type="number"
        value={value}
        onChange={(e) => {
          const n = parseInt(e.target.value, 10);
          onChange(Number.isNaN(n) ? 1 : clamp(n, max));
        }}
        step="1"
        min={1}
        max={max}
      />
    </label>
  );
}
 
function Box({ title, value }) {
  return (
    <div className="p-8 shadow">
      <span className="text-lg">{title}</span>
      <br></br>
      <span className="text-4xl">{value.toFixed(4)}</span>
    </div>
  );
}
 
function FrequencyModelDisplay() {
  const [kmLevel, setKmLevel] = useState(1);
  const [zoneLevel, setZoneLevel] = useState(1);
  const [makeLevel, setMakeLevel] = useState(1);
  const [bonusLevel, setBonusLevel] = useState(1);
 
  const { main, interactions, sum, rate } = computeBreakdown(
    kmLevel, zoneLevel, makeLevel, bonusLevel
  );
 
  return (
    <>
      <div className="flex gap-4 mb-4">
        <NumberInput label="KM" value={kmLevel} max={MAX.km} onChange={setKmLevel} />
        <NumberInput label="Zone" value={zoneLevel} max={MAX.zone} onChange={setZoneLevel} />
        <NumberInput label="Make" value={makeLevel} max={MAX.make} onChange={setMakeLevel} />
        <NumberInput label="Bonus" value={bonusLevel} max={MAX.bonus} onChange={setBonusLevel} />
      </div>
 
      <div className="grid grid-cols-4  mb-4">
        <Box title="Intercept: " value={intercept} />
        <Box title="KM effect: " value={main.km} />
        <Box title="Zone effect: " value={main.zone} />
        <Box title="Make effect: " value={main.make} />
        <Box title="Bonus effect: " value={main.bonus} />
        <Box title="Zone x Bonus: " value={interactions.zoneBonus} />
        <Box title="Make x Bonus: " value={interactions.makeBonus} />
        <Box title="Make x KM: " value={interactions.makeKm} />
      </div>
 
      <div className="grid grid-cols-2 gap-4 mb-4">
        <Box title="Sum (linear predictor): " value={sum} />
        <Box title="Predicted rate (claims per policy-year): " value={rate} />
      </div>
    </>
  );
}
 
export default FrequencyModelDisplay;
